import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useNavigate } from 'react-router-dom';
import { isAdmin, isStaff } from '../../features/auth/authSlice';
import { inviteUsersToTask } from '../../features/taskApplications/taskApplicationSlice';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import { useDebounce } from '../../hooks/useDebounce';
import {
    ClipboardDocumentListIcon,
    PlusIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    CurrencyDollarIcon,
    UserGroupIcon,
    MapPinIcon,
    CalendarIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    AcademicCapIcon,
    UserPlusIcon,
    PlayIcon,
    PauseIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

interface BudgetScenario {
    id: string;
    name: string;
    description: string;
    threshold: {
        type: 'task_count' | 'completion_percentage' | 'revenue_target';
        value: number;
        operator: 'gte' | 'lte' | 'eq';
    };
    budgetDistribution: {
        teamExecutive: number;
        teamLeader: number;
        districtHead: number;
        stateHead: number;
        regionalHead: number;
        admin: number;
    };
    isActive: boolean;
}

interface Task {
    _id: string;
    title: string;
    description: string;
    taskUrl: string;
    totalBudget: number;
    budgetDistribution: {
        teamExecutive: number;
        teamLeader: number;
        districtHead: number;
        stateHead: number;
        regionalHead: number;
        admin: number;
    };
    budgetScenarios: BudgetScenario[];
    currentScenario: string | null;
    taskCompletionMetrics: {
        totalTasksAssigned: number;
        completedTasks: number;
        completionPercentage: number;
        revenueGenerated: number;
        lastUpdated: string;
    };
    regions: string[];
    states: string[];
    districts: string[];
    targetDesignations: string[];
    requiredSkills: Array<{
        _id: string;
        name: string;
        description?: string;
    }>;
    startDate: string;
    endDate: string;
    validationRules: {
        requiredFields: string[];
    };
    status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
    company: {
        _id: string;
        companyName: string;
        logo?: string;
    };
    postedBy: {
        _id: string;
        firstName: string;
        lastName: string;
    };
    assignedUsers: Array<{
        _id: string;
        firstName: string;
        lastName: string;
        designation: string;
        region: string;
        state: string;
        district: string;
    }>;
    submittedData: Array<{
        _id: string;
        userId: string;
        data: any;
        submittedAt: string;
        isValidated: boolean;
        validationScore: number;
    }>;
    createdAt: string;
    updatedAt: string;
}

const AdminTasks: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { user } = useAppSelector((state) => state.auth);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [statusFilter, setStatusFilter] = useState('all');
    const [skillFilter, setSkillFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Helper function to get base path based on user role
    const getBasePath = () => {
        if (user && isStaff(user)) {
            return '/staff';
        }
        return '/admin';
    };

    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [showDetails, setShowDetails] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [skills, setSkills] = useState<Array<{ _id: string; name: string }>>([]);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [availableUsers, setAvailableUsers] = useState<Array<{ _id: string; firstName: string; lastName: string; designation: string; region: string; personalDetails?: { address?: { state?: string; district?: string } }; skills: Array<{ _id: string; name: string }> }>>([]);
    const [editFormData, setEditFormData] = useState({
        title: '',
        description: '',
        taskUrl: '',
        totalBudget: '',
        budgetDistribution: {
            teamExecutive: 0,
            teamLeader: 0,
            districtHead: 0,
            stateHead: 0,
            regionalHead: 0,
            admin: 0,
        },
        budgetScenarios: [] as BudgetScenario[],
        currentScenario: null as string | null,
        regions: [] as string[],
        states: [] as string[],
        districts: [] as string[],
        targetDesignations: [] as string[],
        requiredSkills: [] as string[],
        startDate: '',
        endDate: '',
        validationRules: {
            requiredFields: [] as string[],
        },
    });
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [showBudgetScenarioModal, setShowBudgetScenarioModal] = useState(false);
    const [editingScenario, setEditingScenario] = useState<BudgetScenario | null>(null);
    const [newScenario, setNewScenario] = useState<Partial<BudgetScenario>>({
        name: '',
        description: '',
        threshold: {
            type: 'task_count',
            value: 0,
            operator: 'gte'
        },
        budgetDistribution: {
            teamExecutive: 0,
            teamLeader: 0,
            districtHead: 0,
            stateHead: 0,
            regionalHead: 0,
            admin: 0,
        },
        isActive: true
    });

    const fetchTasks = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: '10',
                search: debouncedSearchTerm,
                status: statusFilter !== 'all' ? statusFilter : '',
                skill: skillFilter !== 'all' ? skillFilter : ''
            });

            // Use different endpoints based on user role
            const endpoint = user && isAdmin(user) ? '/tasks' : '/tasks/user-tasks';
            const response = await api.get(`${endpoint}?${params}`);

            if (response.data.success) {
                setTasks(response.data.data.tasks || []);
                setTotalPages(response.data.data.pagination?.totalPages || 1);
            }
        } catch (error: any) {
            console.error('Error fetching tasks:', error);
            dispatch(showToast({
                message: 'Failed to fetch tasks',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch, currentPage, debouncedSearchTerm, statusFilter, skillFilter, user]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const fetchSkills = useCallback(async () => {
        try {
            const response = await api.get('/skills/all');
            if (response.data.success) {
                setSkills(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching skills:', error);
        }
    }, []);

    useEffect(() => {
        fetchSkills();
    }, [fetchSkills]);

    const handleStatusChange = async (taskId: string, newStatus: string) => {
        try {
            const response = await api.put(`/tasks/${taskId}`, { status: newStatus });

            if (response.data.success) {
                setTasks(prev => prev.map(task =>
                    task._id === taskId ? { ...task, status: newStatus as any } : task
                ));
                dispatch(showToast({
                    message: `Task status updated to ${newStatus}`,
                    type: 'success'
                }));
            }
        } catch (error: any) {
            console.error('Error updating task status:', error);
            dispatch(showToast({
                message: 'Failed to update task status',
                type: 'error'
            }));
        }
    };

    const handleDeleteTask = async (taskId: string) => {
        if (!window.confirm('Are you sure you want to delete this task?')) {
            return;
        }

        try {
            const response = await api.delete(`/tasks/${taskId}`);

            if (response.data.success) {
                setTasks(prev => prev.filter(task => task._id !== taskId));
                dispatch(showToast({
                    message: 'Task deleted successfully',
                    type: 'success'
                }));
            }
        } catch (error: any) {
            console.error('Error deleting task:', error);
            dispatch(showToast({
                message: 'Failed to delete task',
                type: 'error'
            }));
        }
    };

    const fetchAvailableUsers = async (taskId: string) => {
        try {
            console.log('Fetching available users for task:', taskId);
            const response = await api.get(`/users/available-for-task/${taskId}`);
            console.log('API response:', response.data);
            if (response.data.success) {
                console.log('Setting available users:', response.data.data);
                setAvailableUsers(response.data.data || []);
            } else {
                console.error('API returned success: false');
            }
        } catch (error) {
            console.error('Error fetching available users:', error);
            dispatch(showToast({
                message: 'Failed to fetch available users',
                type: 'error'
            }));
        }
    };

    const handleInviteUsers = async () => {
        if (!selectedTask) return;

        if (selectedUsers.length === 0) {
            dispatch(showToast({
                message: 'Please select at least one user to invite',
                type: 'error'
            }));
            return;
        }

        try {
            await dispatch(inviteUsersToTask({
                taskId: selectedTask._id,
                userIds: selectedUsers
            })).unwrap();

            dispatch(showToast({
                message: 'Users invited successfully! They will receive notifications to apply.',
                type: 'success'
            }));

            setShowInviteModal(false);
            setSelectedUsers([]);
            fetchTasks();
        } catch (error: any) {
            dispatch(showToast({
                message: error.message || 'Failed to invite users',
                type: 'error'
            }));
        }
    };

    const handleInviteAllUsers = async () => {
        if (!selectedTask) return;

        try {
            await dispatch(inviteUsersToTask({
                taskId: selectedTask._id,
                userIds: [] // Empty array triggers auto-invite all matching users
            })).unwrap();

            dispatch(showToast({
                message: 'All matching users invited successfully! They will receive notifications to apply.',
                type: 'success'
            }));

            setShowInviteModal(false);
            setSelectedUsers([]);
            fetchTasks();
        } catch (error: any) {
            dispatch(showToast({
                message: error.message || 'Failed to invite users',
                type: 'error'
            }));
        }
    };

    const openInviteModal = (task: Task) => {
        setSelectedTask(task);
        setShowInviteModal(true);
        fetchAvailableUsers(task._id);
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTask) return;

        setIsSubmittingEdit(true);
        try {
            const updateData = {
                ...editFormData,
                totalBudget: parseFloat(editFormData.totalBudget),
                budgetDistribution: editFormData.budgetDistribution,
                validationRules: editFormData.validationRules,
            };

            const response = await api.put(`/tasks/${selectedTask._id}`, updateData);

            if (response.data.success) {
                setTasks(prev => prev.map(task =>
                    task._id === selectedTask._id ? { ...task, ...response.data.data } : task
                ));
                dispatch(showToast({
                    message: 'Task updated successfully!',
                    type: 'success'
                }));
                setShowEditModal(false);
            }
        } catch (error: any) {
            console.error('Error updating task:', error);
            dispatch(showToast({
                message: error?.response?.data?.message || 'Failed to update task',
                type: 'error'
            }));
        } finally {
            setIsSubmittingEdit(false);
        }
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (name.startsWith('budgetDistribution.')) {
            const field = name.split('.')[1];
            setEditFormData(prev => ({
                ...prev,
                budgetDistribution: {
                    ...prev.budgetDistribution,
                    [field]: parseFloat(value) || 0
                }
            }));
        } else if (name.startsWith('validationRules.')) {
            const field = name.split('.')[1];
            setEditFormData(prev => ({
                ...prev,
                validationRules: {
                    ...prev.validationRules,
                    [field]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
                }
            }));
        } else {
            setEditFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
            case 'draft':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
            case 'paused':
                return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
            case 'completed':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'cancelled':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active':
                return <CheckCircleIcon className="h-4 w-4" />;
            case 'draft':
                return <ClockIcon className="h-4 w-4" />;
            case 'paused':
                return <ExclamationTriangleIcon className="h-4 w-4" />;
            case 'completed':
                return <CheckCircleIcon className="h-4 w-4" />;
            case 'cancelled':
                return <ExclamationTriangleIcon className="h-4 w-4" />;
            default:
                return <ClockIcon className="h-4 w-4" />;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    // Budget Scenario Management Functions
    const addBudgetScenario = () => {
        const scenario: BudgetScenario = {
            id: Date.now().toString(),
            name: newScenario.name || '',
            description: newScenario.description || '',
            threshold: newScenario.threshold || {
                type: 'task_count',
                value: 0,
                operator: 'gte'
            },
            budgetDistribution: newScenario.budgetDistribution || {
                teamExecutive: 0,
                teamLeader: 0,
                districtHead: 0,
                stateHead: 0,
                regionalHead: 0,
                admin: 0,
            },
            isActive: newScenario.isActive || true
        };

        setEditFormData(prev => ({
            ...prev,
            budgetScenarios: [...prev.budgetScenarios, scenario]
        }));

        setNewScenario({
            name: '',
            description: '',
            threshold: {
                type: 'task_count',
                value: 0,
                operator: 'gte'
            },
            budgetDistribution: {
                teamExecutive: 0,
                teamLeader: 0,
                districtHead: 0,
                stateHead: 0,
                regionalHead: 0,
                admin: 0,
            },
            isActive: true
        });
        setShowBudgetScenarioModal(false);
    };

    const updateBudgetScenario = (scenarioId: string, updatedScenario: BudgetScenario) => {
        setEditFormData(prev => ({
            ...prev,
            budgetScenarios: prev.budgetScenarios.map(scenario =>
                scenario.id === scenarioId ? updatedScenario : scenario
            )
        }));
        setEditingScenario(null);
        setShowBudgetScenarioModal(false);
    };

    const deleteBudgetScenario = (scenarioId: string) => {
        setEditFormData(prev => ({
            ...prev,
            budgetScenarios: prev.budgetScenarios.filter(scenario => scenario.id !== scenarioId)
        }));
    };

    const activateBudgetScenario = (scenarioId: string) => {
        setEditFormData(prev => ({
            ...prev,
            currentScenario: scenarioId,
            budgetScenarios: prev.budgetScenarios.map(scenario => ({
                ...scenario,
                isActive: scenario.id === scenarioId
            }))
        }));
    };

    const calculateCurrentBudgetDistribution = (task: Task) => {
        if (!task.budgetScenarios || task.budgetScenarios.length === 0) {
            return task.budgetDistribution;
        }

        const activeScenario = task.budgetScenarios.find(scenario => scenario.isActive);
        if (activeScenario) {
            return activeScenario.budgetDistribution;
        }

        return task.budgetDistribution;
    };


    // Calculate stats
    const stats = {
        total: tasks.length,
        active: tasks.filter(task => task.status === 'active').length,
        completed: tasks.filter(task => task.status === 'completed').length,
        totalBudget: tasks.reduce((sum, task) => sum + task.totalBudget, 0),
        totalParticipants: tasks.reduce((sum, task) => sum + task.assignedUsers.length, 0)
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <div className="flex items-center mb-4">
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl mr-4">
                                    <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                        {user && isAdmin(user) ? 'Task Management' : 'My Tasks'}
                                    </h1>
                                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                                        {user && isAdmin(user) ? 'Manage and monitor all platform tasks' : 'View your assigned tasks'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        {user && isAdmin(user) && (
                            <div className="mt-4 sm:mt-0">
                                <button
                                    onClick={() => window.location.href = '/dashboard/tasks/create'}
                                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                                >
                                    <PlusIcon className="h-5 w-5 mr-2" />
                                    Create Task
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center">
                            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-xl">
                                <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tasks</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center">
                            <div className="p-3 bg-emerald-100 dark:bg-emerald-900 rounded-xl">
                                <CheckCircleIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Tasks</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.active}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center">
                            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-xl">
                                <CheckCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.completed}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center">
                            <div className="p-3 bg-yellow-100 dark:bg-yellow-900 rounded-xl">
                                <CurrencyDollarIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Budget</p>
                                <p className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(stats.totalBudget)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center">
                            <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-xl">
                                <UserGroupIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Participants</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalParticipants}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
                    <div className="flex items-center mb-4">
                        <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search tasks..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                            />
                        </div>

                        {/* Status Filter */}
                        <div>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                            >
                                <option value="all">All Status</option>
                                <option value="draft">Draft</option>
                                <option value="active">Active</option>
                                <option value="paused">Paused</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>

                        {/* Skill Filter */}
                        <div>
                            <select
                                value={skillFilter}
                                onChange={(e) => setSkillFilter(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                            >
                                <option value="all">All Skills</option>
                                {skills.map(skill => (
                                    <option key={skill._id} value={skill._id}>
                                        {skill.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Filter Button */}
                        <button
                            onClick={fetchTasks}
                            className="inline-flex items-center justify-center px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-xl transition-all duration-200"
                        >
                            <FunnelIcon className="h-5 w-5 mr-2" />
                            Apply Filters
                        </button>
                    </div>
                </div>

                {/* Tasks Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {tasks.length > 0 ? (
                        tasks.map((task) => (
                            <div key={task._id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-2xl transition-all duration-300 group">
                                {/* Header */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center">
                                        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg group-hover:bg-blue-200 dark:group-hover:bg-blue-800 transition-colors">
                                            <ClipboardDocumentListIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2">
                                                {task.title}
                                            </h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {task.company?.companyName || `${task.postedBy?.firstName || ''} ${task.postedBy?.lastName || ''}`.trim() || 'Unknown'}
                                            </p>
                                        </div>
                                    </div>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                                        {getStatusIcon(task.status)}
                                        <span className="ml-1 capitalize">{task.status}</span>
                                    </span>
                                </div>

                                {/* Skills */}
                                <div className="mb-4">
                                    <div className="flex items-center mb-2">
                                        <AcademicCapIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Required Skills</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {task.requiredSkills.slice(0, 3).map(skill => (
                                            <span key={skill._id} className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                                                {skill.name}
                                            </span>
                                        ))}
                                        {task.requiredSkills.length > 3 && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                                +{task.requiredSkills.length - 3} more
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Stats */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
                                        <CurrencyDollarIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mx-auto mb-1" />
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(task.totalBudget)}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Budget</p>
                                    </div>
                                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
                                        <UserGroupIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 mx-auto mb-1" />
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{task.assignedUsers.length}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Participants</p>
                                    </div>
                                </div>

                                {/* Current Budget Distribution */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Current Distribution</h4>
                                        {task.budgetScenarios && task.budgetScenarios.length > 0 && (
                                            <span className="text-xs text-green-600 dark:text-green-400">
                                                {task.budgetScenarios.filter(s => s.isActive).length} Active Scenario(s)
                                            </span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        {Object.entries(calculateCurrentBudgetDistribution(task)).map(([role, amount]) => (
                                            <div key={role} className="flex justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                                <span className="text-gray-600 dark:text-gray-400 capitalize">
                                                    {role.replace(/([A-Z])/g, ' $1').trim()}
                                                </span>
                                                <span className="text-gray-900 dark:text-white font-medium">
                                                    ₹{amount.toLocaleString()}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Scope */}
                                <div className="mb-4">
                                    <div className="flex items-center mb-2">
                                        <MapPinIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Scope</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                        <span>{task.regions.length} regions</span>
                                        <span className="mx-2">•</span>
                                        <span>{task.states.length} states</span>
                                        <span className="mx-2">•</span>
                                        <span>{task.districts.length} districts</span>
                                    </div>
                                </div>

                                {/* Timeline */}
                                <div className="mb-6">
                                    <div className="flex items-center mb-2">
                                        <CalendarIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Timeline</span>
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        <p>Start: {formatDate(task.startDate)}</p>
                                        <p>End: {formatDate(task.endDate)}</p>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => navigate(`${getBasePath()}/tasks/${task._id}`)}
                                            className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                            title="View Details"
                                        >
                                            <EyeIcon className="h-4 w-4" />
                                        </button>

                                        {(user && isAdmin(user)) || user?.role === 'recruiter' ? (
                                            <button
                                                onClick={() => openInviteModal(task)}
                                                className="p-2 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                                                title="Invite Users"
                                            >
                                                <UserPlusIcon className="h-4 w-4" />
                                            </button>
                                        ) : null}
                                        {user && isAdmin(user) || user?.role === 'recruiter' && (
                                            <button
                                                onClick={() => navigate(`${getBasePath()}/tasks/${task._id}/applications`)}
                                                className="p-2 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                                                title="View Applications"
                                            >
                                                <UserGroupIcon className="h-4 w-4" />
                                            </button>
                                        )}
                                        {user && isAdmin(user) && (
                                            <>
                                                <button
                                                    key={`edit-${task._id}`}
                                                    onClick={() => navigate(`${getBasePath()}/tasks/${task._id}/edit`)}
                                                    className="p-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                                    title="Edit Task"
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button
                                                    key={`status-${task._id}`}
                                                    onClick={() => handleStatusChange(task._id, task.status === 'active' ? 'paused' : 'active')}
                                                    className={`p-2 rounded-lg transition-colors ${task.status === 'active'
                                                        ? 'text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                                                        : 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                                                        }`}
                                                    title={task.status === 'active' ? 'Pause Task' : 'Activate Task'}
                                                >
                                                    {task.status === 'active' ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                                                </button>
                                                <button
                                                    key={`delete-${task._id}`}
                                                    onClick={() => handleDeleteTask(task._id)}
                                                    className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                    title="Delete Task"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        Created {formatDate(task.createdAt)}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-full">
                            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
                                <ClipboardDocumentListIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No tasks found</h3>
                                <p className="text-gray-500 dark:text-gray-400 mb-6">Get started by creating your first task</p>
                                <button
                                    onClick={() => window.location.href = '/dashboard/tasks/create'}
                                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                                >
                                    <PlusIcon className="h-5 w-5 mr-2" />
                                    Create Task
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-8 flex justify-center">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4">
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Previous
                                </button>

                                <div className="flex items-center space-x-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        const page = i + 1;
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${currentPage === page
                                                    ? 'bg-blue-600 text-white'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Invitation Modal */}
            {showInviteModal && selectedTask && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Invite Users to Task
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {selectedTask.title}
                                </p>
                                <div className="mt-2">
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Required Skills: </span>
                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                        {selectedTask.requiredSkills?.map(skill => skill.name).join(', ')}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowInviteModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            {/* Debug Info */}
                            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                    Debug: Available users count: {availableUsers.length}
                                </p>
                                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                    Task ID: {selectedTask._id}
                                </p>
                            </div>

                            {/* Quick Actions */}
                            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                                            Quick Invite All Matching Users
                                        </h4>
                                        <p className="text-xs text-green-700 dark:text-green-300">
                                            Automatically invite all users with matching skills and location criteria
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleInviteAllUsers}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
                                    >
                                        <UserGroupIcon className="h-4 w-4" />
                                        Invite All ({availableUsers.length})
                                    </button>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Manual Selection ({selectedUsers.length} selected)
                                </h4>
                                <div className="space-y-3">
                                    {availableUsers.map(user => {
                                        const matchingSkills = user.skills ?
                                            selectedTask.requiredSkills?.filter(requiredSkill =>
                                                user.skills.some((userSkill: any) => userSkill._id === requiredSkill._id)
                                            ) || [] : [];
                                        const isPerfectMatch = matchingSkills.length > 0;

                                        return (
                                            <div key={user._id} className={`p-4 rounded-xl border transition-all ${isPerfectMatch
                                                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                                : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                                                }`}>
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-start">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedUsers.includes(user._id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedUsers([...selectedUsers, user._id]);
                                                                } else {
                                                                    setSelectedUsers(selectedUsers.filter(id => id !== user._id));
                                                                }
                                                            }}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3 mt-1"
                                                        />
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                                    {user.firstName} {user.lastName}
                                                                </p>
                                                                {isPerfectMatch && (
                                                                    <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                                                                        Skill Match
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                                                {user.designation} • {user.region} • {user.personalDetails?.address?.state} • {user.personalDetails?.address?.district}
                                                            </p>

                                                            {/* User Skill */}
                                                            <div className="mb-2">
                                                                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                                                    User Skill:
                                                                </span>
                                                                <div className="flex flex-wrap gap-1 mt-1">
                                                                    {user.skills && user.skills.length > 0 && (
                                                                        user.skills.map((skill: any) => (
                                                                            <span key={skill._id} className={`px-2 py-1 rounded-full text-xs font-medium ${isPerfectMatch
                                                                                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                                                                                : 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                                                                                }`}>
                                                                                {skill.name}
                                                                            </span>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {availableUsers.length === 0 && (
                                        <div className="text-center py-8">
                                            <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                            <p className="text-gray-500 dark:text-gray-400">No available users found</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => setShowInviteModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleInviteAllUsers}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                            >
                                Invite All Matching Users
                            </button>
                            <button
                                onClick={handleInviteUsers}
                                disabled={selectedUsers.length === 0}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
                            >
                                Invite {selectedUsers.length} User{selectedUsers.length !== 1 ? 's' : ''}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Task Details Modal */}
            {showDetails && selectedTask && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Task Details</h2>
                                <button
                                    onClick={() => setShowDetails(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Basic Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Basic Information</h3>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Title</label>
                                                <p className="text-gray-900 dark:text-white">{selectedTask.title}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</label>
                                                <div className="text-gray-900 dark:text-white whitespace-pre-wrap">{selectedTask.description}</div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Task URL</label>
                                                <a href={selectedTask.taskUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                                    {selectedTask.taskUrl}
                                                </a>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Budget</label>
                                                <p className="text-gray-900 dark:text-white">₹{selectedTask.totalBudget?.toLocaleString() || '0'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Scope & Timeline</h3>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Regions</label>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {selectedTask.regions?.map(region => (
                                                        <span key={region} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-xs">
                                                            {region}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">States</label>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {selectedTask.states?.slice(0, 5).map(state => (
                                                        <span key={state} className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-xs">
                                                            {state}
                                                        </span>
                                                    ))}
                                                    {selectedTask.states?.length > 5 && (
                                                        <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-full text-xs">
                                                            +{selectedTask.states.length - 5} more
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Start Date</label>
                                                <p className="text-gray-900 dark:text-white">{formatDate(selectedTask.startDate)}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">End Date</label>
                                                <p className="text-gray-900 dark:text-white">{formatDate(selectedTask.endDate)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Task Completion Metrics */}
                                {selectedTask.taskCompletionMetrics && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Task Completion Metrics</h3>
                                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-4">
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div className="text-center">
                                                    <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                                        {selectedTask.taskCompletionMetrics.completedTasks}
                                                    </div>
                                                    <div className="text-sm text-blue-600 dark:text-blue-400">Completed Tasks</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                                        {selectedTask.taskCompletionMetrics.totalTasksAssigned}
                                                    </div>
                                                    <div className="text-sm text-blue-600 dark:text-blue-400">Total Assigned</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                                        {selectedTask.taskCompletionMetrics.completionPercentage}%
                                                    </div>
                                                    <div className="text-sm text-blue-600 dark:text-blue-400">Completion Rate</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                                        ₹{selectedTask.taskCompletionMetrics.revenueGenerated.toLocaleString()}
                                                    </div>
                                                    <div className="text-sm text-blue-600 dark:text-blue-400">Revenue Generated</div>
                                                </div>
                                            </div>
                                            <div className="mt-3 text-xs text-blue-700 dark:text-blue-300">
                                                Last Updated: {formatDate(selectedTask.taskCompletionMetrics.lastUpdated)}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Current Budget Distribution */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Current Budget Distribution</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {Object.entries(calculateCurrentBudgetDistribution(selectedTask)).map(([role, amount]) => (
                                            <div key={role} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 capitalize">
                                                    {role.replace(/([A-Z])/g, ' $1').trim()}
                                                </div>
                                                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    ₹{amount?.toLocaleString() || '0'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Budget Scenarios */}
                                {selectedTask.budgetScenarios && selectedTask.budgetScenarios.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Budget Scenarios</h3>
                                        <div className="space-y-4">
                                            {selectedTask.budgetScenarios.map((scenario) => (
                                                <div key={scenario.id} className={`p-4 rounded-xl border ${scenario.isActive
                                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                                                    }`}>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div>
                                                            <h4 className="font-semibold text-gray-900 dark:text-white">{scenario.name}</h4>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">{scenario.description}</p>
                                                            <div className="mt-1">
                                                                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                                                    Threshold: {scenario.threshold.type.replace('_', ' ')} {scenario.threshold.operator} {scenario.threshold.value}
                                                                </span>
                                                                {scenario.isActive && (
                                                                    <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                                                                        Active
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                        {Object.entries(scenario.budgetDistribution).map(([role, amount]) => (
                                                            <div key={role} className="bg-white dark:bg-gray-600 p-2 rounded-lg">
                                                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 capitalize">
                                                                    {role.replace(/([A-Z])/g, ' $1').trim()}
                                                                </div>
                                                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                                    ₹{amount.toLocaleString()}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Required Skills */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Required Skills</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedTask.requiredSkills?.map(skill => (
                                            <span key={skill._id} className="px-3 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full text-sm">
                                                {skill.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Target Designations */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Target Designations</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedTask.targetDesignations?.map(designation => (
                                            <span key={designation} className="px-3 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded-full text-sm">
                                                {designation.replace('_', ' ')}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Validation Rules */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Validation Rules</h3>
                                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                        <div>
                                            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Required Fields</label>
                                            <div className="flex flex-wrap gap-2 mt-1">
                                                {selectedTask.validationRules?.requiredFields?.map(field => (
                                                    <span key={field} className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full text-xs">
                                                        {field}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Task Edit Modal */}
            {showEditModal && selectedTask && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Task</h2>
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <form onSubmit={handleEditSubmit} className="space-y-6">
                                {/* Basic Information */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Task Title *
                                        </label>
                                        <input
                                            type="text"
                                            id="edit-title"
                                            name="title"
                                            value={editFormData.title}
                                            onChange={handleEditChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="edit-totalBudget" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Total Budget Per Task(₹) *
                                        </label>
                                        <input
                                            type="number"
                                            id="edit-totalBudget"
                                            name="totalBudget"
                                            value={editFormData.totalBudget}
                                            onChange={handleEditChange}
                                            min="1"
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Description *
                                    </label>
                                    <textarea
                                        id="edit-description"
                                        name="description"
                                        value={editFormData.description}
                                        onChange={handleEditChange}
                                        rows={4}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="edit-taskUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Task URL *
                                    </label>
                                    <input
                                        type="url"
                                        id="edit-taskUrl"
                                        name="taskUrl"
                                        value={editFormData.taskUrl}
                                        onChange={handleEditChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    />
                                </div>

                                {/* Budget Distribution */}
                                <div>


                                    {/* Default Budget Distribution */}
                                    <div className="mb-6">
                                        <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">Default Distribution</h4>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                            {Object.entries(editFormData.budgetDistribution).map(([role, amount]) => (
                                                <div key={role}>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 capitalize">
                                                        {role.replace(/([A-Z])/g, ' $1').trim()}
                                                    </label>
                                                    <input
                                                        type="number"
                                                        name={`budgetDistribution.${role}`}
                                                        value={amount}
                                                        onChange={handleEditChange}
                                                        min="0"
                                                        step="0.01"
                                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Budget Distribution</h3>
                                        <button
                                            type="button"
                                            onClick={() => setShowBudgetScenarioModal(true)}
                                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                                        >
                                            Add Scenario
                                        </button>
                                    </div>
                                    {/* Budget Scenarios */}
                                    {editFormData.budgetScenarios.length > 0 && (
                                        <div>
                                            <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">Budget Scenarios</h4>
                                            <div className="space-y-4">
                                                {editFormData.budgetScenarios.map((scenario, index) => (
                                                    <div key={scenario.id} className={`p-4 rounded-xl border ${scenario.isActive
                                                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                                                        }`}>
                                                        <div className="flex items-center justify-between mb-3">
                                                            <div>
                                                                <h5 className="font-semibold text-gray-900 dark:text-white">{scenario.name}</h5>
                                                                <p className="text-sm text-gray-600 dark:text-gray-400">{scenario.description}</p>
                                                                <div className="mt-1">
                                                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                                                        Threshold: {scenario.threshold.type.replace('_', ' ')} {scenario.threshold.operator} {scenario.threshold.value}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center space-x-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => activateBudgetScenario(scenario.id)}
                                                                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${scenario.isActive
                                                                        ? 'bg-green-600 text-white'
                                                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                                        }`}
                                                                >
                                                                    {scenario.isActive ? 'Active' : 'Activate'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setEditingScenario(scenario);
                                                                        setShowBudgetScenarioModal(true);
                                                                    }}
                                                                    className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                                >
                                                                    <PencilIcon className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => deleteBudgetScenario(scenario.id)}
                                                                    className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                                >
                                                                    <TrashIcon className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                            {Object.entries(scenario.budgetDistribution).map(([role, amount]) => (
                                                                <div key={role}>
                                                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 capitalize">
                                                                        {role.replace(/([A-Z])/g, ' $1').trim()}
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        value={amount}
                                                                        readOnly
                                                                        className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white"
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Required Skills */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Required Skills *
                                    </label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {skills.map(skill => (
                                            <label key={skill._id} className="flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={editFormData.requiredSkills.includes(skill._id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setEditFormData(prev => ({
                                                                ...prev,
                                                                requiredSkills: [...prev.requiredSkills, skill._id]
                                                            }));
                                                        } else {
                                                            setEditFormData(prev => ({
                                                                ...prev,
                                                                requiredSkills: prev.requiredSkills.filter(id => id !== skill._id)
                                                            }));
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">{skill.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Regions */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Regions *
                                    </label>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                        {['north', 'south', 'east', 'west', 'central'].map(region => (
                                            <label key={region} className="flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={editFormData.regions.includes(region)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setEditFormData(prev => ({
                                                                ...prev,
                                                                regions: [...prev.regions, region]
                                                            }));
                                                        } else {
                                                            setEditFormData(prev => ({
                                                                ...prev,
                                                                regions: prev.regions.filter(r => r !== region)
                                                            }));
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{region}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* States */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        States
                                    </label>
                                    <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2">
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                                            {['Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir'].map(state => (
                                                <label key={state} className="flex items-center p-1 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={editFormData.states.includes(state)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setEditFormData(prev => ({
                                                                    ...prev,
                                                                    states: [...prev.states, state]
                                                                }));
                                                            } else {
                                                                setEditFormData(prev => ({
                                                                    ...prev,
                                                                    states: prev.states.filter(s => s !== state)
                                                                }));
                                                            }
                                                        }}
                                                        className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-1"
                                                    />
                                                    <span className="text-xs text-gray-700 dark:text-gray-300">{state}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Target Designations */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Target Designations *
                                    </label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {['team_executive', 'team_leader', 'district_head', 'state_head', 'regional_head'].map(designation => (
                                            <label key={designation} className="flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={editFormData.targetDesignations.includes(designation)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setEditFormData(prev => ({
                                                                ...prev,
                                                                targetDesignations: [...prev.targetDesignations, designation]
                                                            }));
                                                        } else {
                                                            setEditFormData(prev => ({
                                                                ...prev,
                                                                targetDesignations: prev.targetDesignations.filter(d => d !== designation)
                                                            }));
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                                                    {designation.replace('_', ' ')}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Validation Rules */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Validation Rules</h3>
                                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Required Fields
                                            </label>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                {['studentName', 'studentEmail', 'studentPhone', 'studentAddress', 'studentAge', 'studentGender', 'studentQualification', 'studentCourse', 'studentCollege', 'studentYear'].map(field => (
                                                    <label key={field} className="flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={editFormData.validationRules.requiredFields.includes(field)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setEditFormData(prev => ({
                                                                        ...prev,
                                                                        validationRules: {
                                                                            ...prev.validationRules,
                                                                            requiredFields: [...prev.validationRules.requiredFields, field]
                                                                        }
                                                                    }));
                                                                } else {
                                                                    setEditFormData(prev => ({
                                                                        ...prev,
                                                                        validationRules: {
                                                                            ...prev.validationRules,
                                                                            requiredFields: prev.validationRules.requiredFields.filter(f => f !== field)
                                                                        }
                                                                    }));
                                                                }
                                                            }}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                                                        />
                                                        <span className="text-sm text-gray-700 dark:text-gray-300">{field}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Timeline */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="edit-startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Start Date *
                                        </label>
                                        <input
                                            type="date"
                                            id="edit-startDate"
                                            name="startDate"
                                            value={editFormData.startDate}
                                            onChange={handleEditChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="edit-endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            End Date *
                                        </label>
                                        <input
                                            type="date"
                                            id="edit-endDate"
                                            name="endDate"
                                            value={editFormData.endDate}
                                            onChange={handleEditChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        />
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => setShowEditModal(false)}
                                        className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmittingEdit}
                                        className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-xl transition-colors"
                                    >
                                        {isSubmittingEdit ? 'Updating...' : 'Update Task'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Budget Scenario Modal */}
            {showBudgetScenarioModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {editingScenario ? 'Edit Budget Scenario' : 'Add Budget Scenario'}
                                </h2>
                                <button
                                    onClick={() => {
                                        setShowBudgetScenarioModal(false);
                                        setEditingScenario(null);
                                    }}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Scenario Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Scenario Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={editingScenario?.name || newScenario.name || ''}
                                            onChange={(e) => {
                                                if (editingScenario) {
                                                    setEditingScenario({ ...editingScenario, name: e.target.value });
                                                } else {
                                                    setNewScenario({ ...newScenario, name: e.target.value });
                                                }
                                            }}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Threshold Type *
                                        </label>
                                        <select
                                            value={editingScenario?.threshold.type || newScenario.threshold?.type || 'task_count'}
                                            onChange={(e) => {
                                                const thresholdType = e.target.value as 'task_count' | 'completion_percentage' | 'revenue_target';
                                                if (editingScenario) {
                                                    setEditingScenario({
                                                        ...editingScenario,
                                                        threshold: { ...editingScenario.threshold, type: thresholdType }
                                                    });
                                                } else {
                                                    setNewScenario({
                                                        ...newScenario,
                                                        threshold: { ...newScenario.threshold!, type: thresholdType }
                                                    });
                                                }
                                            }}
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="task_count">Task Count</option>
                                            <option value="completion_percentage">Completion Percentage</option>
                                            <option value="revenue_target">Revenue Target</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        value={editingScenario?.description || newScenario.description || ''}
                                        onChange={(e) => {
                                            if (editingScenario) {
                                                setEditingScenario({ ...editingScenario, description: e.target.value });
                                            } else {
                                                setNewScenario({ ...newScenario, description: e.target.value });
                                            }
                                        }}
                                        rows={3}
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Threshold Settings */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Threshold Value *
                                        </label>
                                        <input
                                            type="number"
                                            value={editingScenario?.threshold.value || newScenario.threshold?.value || 0}
                                            onChange={(e) => {
                                                const value = parseFloat(e.target.value) || 0;
                                                if (editingScenario) {
                                                    setEditingScenario({
                                                        ...editingScenario,
                                                        threshold: { ...editingScenario.threshold, value }
                                                    });
                                                } else {
                                                    setNewScenario({
                                                        ...newScenario,
                                                        threshold: { ...newScenario.threshold!, value }
                                                    });
                                                }
                                            }}
                                            min="0"
                                            step="0.01"
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Operator *
                                        </label>
                                        <select
                                            value={editingScenario?.threshold.operator || newScenario.threshold?.operator || 'gte'}
                                            onChange={(e) => {
                                                const operator = e.target.value as 'gte' | 'lte' | 'eq';
                                                if (editingScenario) {
                                                    setEditingScenario({
                                                        ...editingScenario,
                                                        threshold: { ...editingScenario.threshold, operator }
                                                    });
                                                } else {
                                                    setNewScenario({
                                                        ...newScenario,
                                                        threshold: { ...newScenario.threshold!, operator }
                                                    });
                                                }
                                            }}
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="gte">Greater than or equal (≥)</option>
                                            <option value="lte">Less than or equal (≤)</option>
                                            <option value="eq">Equal to (=)</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Budget Distribution */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Budget Distribution</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {Object.entries(editingScenario?.budgetDistribution || newScenario.budgetDistribution || {}).map(([role, amount]) => (
                                            <div key={role}>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 capitalize">
                                                    {role.replace(/([A-Z])/g, ' $1').trim()}
                                                </label>
                                                <input
                                                    type="number"
                                                    value={amount}
                                                    onChange={(e) => {
                                                        const value = parseFloat(e.target.value) || 0;
                                                        const currentDistribution = editingScenario?.budgetDistribution || newScenario.budgetDistribution || {
                                                            teamExecutive: 0,
                                                            teamLeader: 0,
                                                            districtHead: 0,
                                                            stateHead: 0,
                                                            regionalHead: 0,
                                                            admin: 0,
                                                        };
                                                        const updatedDistribution = {
                                                            ...currentDistribution,
                                                            [role]: value
                                                        };
                                                        if (editingScenario) {
                                                            setEditingScenario({
                                                                ...editingScenario,
                                                                budgetDistribution: updatedDistribution
                                                            });
                                                        } else {
                                                            setNewScenario({
                                                                ...newScenario,
                                                                budgetDistribution: updatedDistribution
                                                            });
                                                        }
                                                    }}
                                                    min="0"
                                                    step="0.01"
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowBudgetScenarioModal(false);
                                            setEditingScenario(null);
                                        }}
                                        className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (editingScenario) {
                                                updateBudgetScenario(editingScenario.id, editingScenario);
                                            } else {
                                                addBudgetScenario();
                                            }
                                        }}
                                        className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors"
                                    >
                                        {editingScenario ? 'Update Scenario' : 'Add Scenario'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTasks;